<template>
  <div>
    <div class="form-check mb-2">
      <input
        :id="checkboxId"
        v-model="ignored"
        class="form-check-input"
        type="checkbox"
        data-testid="ignore-sales-strategy"
      />
      <label class="form-check-label fw-semibold" :for="checkboxId">
        {{ $t("forwards.sales_strategy.override.label") }}
      </label>
    </div>

    <div
      v-if="overage !== null"
      class="alert alert-danger d-flex gap-2 mb-3"
      role="alert"
      data-testid="sales-strategy-banner"
    >
      <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" class="mt-1" />
      <div>
        <strong>{{
          $t("forwards.sales_strategy.override.banner_title")
        }}</strong>
        <p class="mb-0">{{ overageLine(overage) }}</p>
      </div>
    </div>

    <p
      v-else-if="ignored && figuresUnknown"
      class="advisory-note mb-3"
      role="status"
      data-testid="sales-strategy-figures-unknown"
    >
      <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
      <span>{{
        $t("forwards.sales_strategy.override.ignored_figures_unknown")
      }}</span>
    </p>

    <p
      v-else-if="ignored"
      class="advisory-note mb-3"
      role="status"
      data-testid="sales-strategy-ignored"
    >
      <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
      <span>{{
        $t("forwards.sales_strategy.override.ignored_nothing_beyond")
      }}</span>
    </p>
  </div>
</template>

<script setup lang="ts">
import {
  type StrategyOverage,
  useSalesStrategyText,
} from "@/forwards2/salesStrategy";
import { useId } from "vue";

/**
 * The sales strategy override, as the deal page's back and resize dialogs show it to a super admin
 * (the picker has none): the DANGEROUS checkbox, and while it is ticked, the red banner naming what
 * the take puts beyond its mandate's strategy — or, with nothing beyond it yet, a warning line, so the
 * box can never be left ticked unnoticed. Where the caller lacks the strategy's figures
 * (`figuresUnknown`), the warning says so rather than claim nothing is beyond it.
 *
 * The caller computes the overage, since only it knows its take, and owns the checkbox state so that
 * it starts unticked whenever its dialog opens.
 */
withDefaults(
  defineProps<{
    /** What the take puts beyond the strategy while the box is ticked; null for nothing. */
    overage: StrategyOverage | null;
    figuresUnknown?: boolean;
  }>(),
  { figuresUnknown: false }
);

const ignored = defineModel<boolean>({ required: true });

const checkboxId = useId();
const { overageLine } = useSalesStrategyText();
</script>
